import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stepper,
  Button,
  Group,
  Container,
  Paper,
  Title,
  Stack,
  LoadingOverlay,
  Text,
  SimpleGrid,
  Box,
  ThemeIcon,
  Center,
} from "@mantine/core";
import {
  IconBuildingStore,
  IconWorldWww,
  IconChecklist,
} from "@tabler/icons-react";

import EventSelectionStep from "./EventSelectionStep";
import StallDetailsStep from "./stallDetailsStep";
import PaymentDetailsStep from "./PaymentDetailsStep";
import CatalogStep from "./CatalogStep";
import { API_BASE_URL } from "../../../config/baseUrls";

export default function MerchantOnboarding() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);

  // Unified Transient State matching our schema needs
  const [formData, setFormData] = useState({
    setupType: "EVENT", // "EVENT" or "ONLINE"
    linkedProductId: null,
    name: "",
    description: "",
    logoUrl: "",
    bannerImageUrl: "",
    socialLinks: { instagram: "", facebook: "", twitter: "", website: "" },
    paymentDetails: {
      accountNumber: "",
      ifscCode: "",
      upiId: "",
      razorpayAccountId: "",
    },
  });

  const handleNext = async () => {
    // Step 0: Branching logic for Setup Type
    if (activeStep === 0) {
      setActiveStep(1);
      return;
    }

    // Step 1: Select Event (Only matters if EVENT, if ONLINE they skip selection inside step)
    if (activeStep === 1) {
      setActiveStep(2);
      return;
    }

    // Step 2: Brand & Social Details -> Submit Main Application to get ID
    if (activeStep === 2) {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/shop-owner/onboarding/apply`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("shopToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: formData.name,
              setupType: formData.setupType,
              linkedProductId:
                formData.setupType === "EVENT"
                  ? formData.linkedProductId
                  : null,
              branding: {
                description: formData.description,
                logoUrl: formData.logoUrl,
                bannerImageUrl: formData.bannerImageUrl,
                socialLinks: formData.socialLinks,
              },
              paymentDetails: formData.paymentDetails,
            }),
          },
        );
        const data = await res.json();

        if (data.success) {
          setApplicationId(data.applicationId);
          setActiveStep(3);
        } else {
          alert(data.error || "Failed to submit onboarding application");
        }
      } catch (err) {
        alert("Network error submitting application");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Step 3: Catalog Upload Step
    if (activeStep === 3) {
      setActiveStep(4);
      return;
    }
  };

  return (
    <Container size="lg" py="xl">
      <Stack align="center" mb="xl">
        <Title order={1} size="h1" ta="center">
          Merchant Partner Onboarding
        </Title>
        <Text size="lg" c="dimmed" ta="center" maw={600}>
          Complete these simple steps to set up your store, configure your
          payouts, and start selling.
        </Text>
      </Stack>

      <Stepper active={activeStep} mb="xl" breakpoint="sm" size="md">
        <Stepper.Step label="Store Type" description="Online or Offline" />
        <Stepper.Step label="Event Target" description="Link physical venue" />
        <Stepper.Step label="Brand & Payouts" description="Setup identity" />
        <Stepper.Step label="Initial Catalog" description="Add inventory" />
        <Stepper.Step label="Under Review" description="Awaiting activation" />
      </Stepper>

      <Paper
        withBorder
        p={{ base: "md", sm: "xl" }}
        radius="lg"
        shadow="sm"
        pos="relative"
      >
        <LoadingOverlay
          visible={loading}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 2 }}
        />

        {/* =========================================
            STEP 0: SETUP TYPE SELECTION
            ========================================= */}
        {activeStep === 0 && (
          <Stack gap="lg">
            <Box>
              <Title order={3} size="h2" mb="xs">
                Where will you sell your products?
              </Title>
              <Text size="lg" c="dimmed">
                Choose if you are setting up a physical booth at an event, or if
                you just want to sell online.
              </Text>
            </Box>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb="md">
              {/* Card Option 1: Offline Event */}
              <Paper
                withBorder
                p="xl"
                radius="md"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, setupType: "EVENT" }))
                }
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  borderColor:
                    formData.setupType === "EVENT"
                      ? "var(--mantine-color-blue-6)"
                      : "var(--mantine-color-gray-3)",
                  backgroundColor:
                    formData.setupType === "EVENT"
                      ? "var(--mantine-color-blue-0)"
                      : "white",
                }}
                className="hover:shadow-md"
              >
                <Group gap="md" align="flex-start" wrap="nowrap">
                  <ThemeIcon
                    size={50}
                    radius="md"
                    color={formData.setupType === "EVENT" ? "blue" : "gray"}
                  >
                    <IconBuildingStore size={28} />
                  </ThemeIcon>
                  <Box>
                    <Title order={4} size="h3" mb={4}>
                      Physical Stall
                    </Title>
                    <Text size="md" c="dimmed">
                      I am setting up a physical booth at an upcoming offline
                      event and want a QR code for customers to order.
                    </Text>
                  </Box>
                </Group>
              </Paper>

              {/* Card Option 2: Online Only */}
              <Paper
                withBorder
                p="xl"
                radius="md"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, setupType: "ONLINE" }))
                }
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  borderColor:
                    formData.setupType === "ONLINE"
                      ? "var(--mantine-color-blue-6)"
                      : "var(--mantine-color-gray-3)",
                  backgroundColor:
                    formData.setupType === "ONLINE"
                      ? "var(--mantine-color-blue-0)"
                      : "white",
                }}
                className="hover:shadow-md"
              >
                <Group gap="md" align="flex-start" wrap="nowrap">
                  <ThemeIcon
                    size={50}
                    radius="md"
                    color={formData.setupType === "ONLINE" ? "blue" : "gray"}
                  >
                    <IconWorldWww size={28} />
                  </ThemeIcon>
                  <Box>
                    <Title order={4} size="h3" mb={4}>
                      Online Store Only
                    </Title>
                    <Text size="md" c="dimmed">
                      I just want a digital storefront to sell my products
                      directly to customers over the internet.
                    </Text>
                  </Box>
                </Group>
              </Paper>
            </SimpleGrid>

            <Group justify="flex-end">
              <Button size="xl" onClick={handleNext}>
                Continue Setup
              </Button>
            </Group>
          </Stack>
        )}

        {/* Step 1: Event Selection Mapping */}
        {activeStep === 1 && (
          <EventSelectionStep
            formData={formData}
            setFormData={setFormData}
            onNext={handleNext}
            onBack={() => setActiveStep(0)}
          />
        )}

        {/* Step 2: Combined Brand Details & Payment Setup */}
        {activeStep === 2 && (
          <Stack gap="xl">
            <Box>
              <Title order={3} size="h2" mb="xs">
                Brand & Payment Details
              </Title>
              <Text size="lg" c="dimmed">
                Set up how your store looks to customers and where you want your
                earnings to be deposited.
              </Text>
            </Box>

            <StallDetailsStep formData={formData} setFormData={setFormData} />
            <PaymentDetailsStep formData={formData} setFormData={setFormData} />

            <Group
              justify="space-between"
              mt="xl"
              pt="md"
              style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
            >
              <Button
                size="lg"
                variant="default"
                onClick={() => setActiveStep(1)}
              >
                Go Back
              </Button>
              <Button size="lg" onClick={handleNext}>
                Save & Continue to Catalog
              </Button>
            </Group>
          </Stack>
        )}

        {/* Step 3: Isolated Catalog Upload using the Application ID Bridge */}
        {activeStep === 3 && (
          <Stack gap="lg">
            <Box>
              <Title order={3} size="h2" mb="xs">
                Upload Your Menu
              </Title>
              <Text size="lg" c="dimmed">
                Add the products or services you want to sell. You can always
                add more later from your dashboard.
              </Text>
            </Box>

            <CatalogStep applicationId={applicationId} />

            <Group justify="flex-end" mt="xl">
              <Button size="xl" color="green" onClick={handleNext}>
                Submit Application for Review
              </Button>
            </Group>
          </Stack>
        )}

        {/* Step 4: Completion / Pending View */}
        {activeStep === 4 && (
          <Center py={40}>
            <Stack align="center" ta="center" gap="lg" maw={600}>
              <ThemeIcon size={80} radius="100%" color="blue" variant="light">
                <IconChecklist size={40} />
              </ThemeIcon>

              <Title order={2} size="h1" c="blue.9">
                Application Under Review
              </Title>

              <Text size="lg" c="dimmed">
                Your application has been safely registered. Our operations team
                will verify your branding and payment details. Once verified,
                your live store dashboard and unique shop QR code will be
                generated automatically.
              </Text>

              <Button
                size="xl"
                variant="outline"
                onClick={() => navigate("/shop-owner/dashboard")}
                mt="xl"
              >
                Return to Merchant Portal
              </Button>
            </Stack>
          </Center>
        )}
      </Paper>
    </Container>
  );
}
